import { useMemo } from "react";

import { Spacing, Text } from "@otta/design";
import { Tabs } from "@otta/search/components/Tabs";
import {
  BrandAssetType,
  BrandAssetWidth,
  CompanyFragment,
  CompanyProfileLinkFragment,
} from "@otta/search/schema";
import { TeamSize } from "@otta/search/components/MeetTheTeam/TeamSize";
import { MissionCard } from "@otta/search/components/MeetTheTeam/MissionCard";
import { Asset } from "@otta/search/components/AssetSprinkler/Asset";
import { AssetSprinkler } from "@otta/search/components/AssetSprinkler";

// just a short alias for my sanity
type AssetLink = CompanyProfileLinkFragment;

/**
 * A bit nasty but if the first N assets are full-width videos
 * with titles fully set, we put them into tabs separately
 */
function splitVideos(als: AssetLink[]): [AssetLink[], AssetLink[]] {
  const items: [AssetLink[], AssetLink[]] = [[], []];
  let current = items[0];

  for (const asset of als) {
    if (
      asset.desktopPlacement?.width !== BrandAssetWidth.Full ||
      asset.mobilePlacement?.width !== BrandAssetWidth.Full ||
      asset.companyBrandAsset.type !== BrandAssetType.Vimeo ||
      !asset.companyBrandAsset.shortTitle ||
      !asset.companyBrandAsset.longTitle
    ) {
      current = items[1];
    }
    current.push(asset);
  }

  if (items[0].length === 1) {
    items[1].push(items[0].pop() as AssetLink);
  }

  return items;
}

export function MeetTheTeam({
  teams,
}: {
  teams: CompanyFragment["teams"];
}): React.ReactElement {
  return (
    <Spacing size={1}>
      <Text bold size={1} data-testid="meet-the-team">
        Meet the team
      </Text>
      <Tabs
        tabs={teams.map(team => ({
          id: team.id,
          title: team.name,
          content: <MeetOneTeam team={team} />,
        }))}
      ></Tabs>
    </Spacing>
  );
}

function VideoTab({ asset }: { asset: AssetLink }): React.ReactElement {
  return (
    <Spacing>
      <Text>{asset.companyBrandAsset.longTitle}</Text>
      <Asset asset={asset} />
    </Spacing>
  );
}

function MeetOneTeam({
  team,
}: {
  team: CompanyFragment["teams"][0];
}): React.ReactElement {
  const [videos, other] = useMemo(
    () => splitVideos(team.brandAssetLinks),
    [team]
  );

  const videoTabs = useMemo(
    () =>
      videos.map(video => ({
        title: `${video.companyBrandAsset.shortTitle}`,
        content: <VideoTab asset={video} />,
        id: `team-video-${video.id}`,
      })),
    [videos]
  );

  return (
    <Spacing size={1}>
      {!!team.size && <TeamSize size={team.size} />}
      {!!team.mission && <MissionCard>{team.mission}</MissionCard>}
      {!!videoTabs.length && <Tabs tabs={videoTabs} />}
      {!!other.length && <AssetSprinkler assets={other} />}
    </Spacing>
  );
}
